import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
  MenuItem,
  Collapse,
  Link,
} from '@mui/material';
import { Star as StarIcon } from '@mui/icons-material';
import ApiService from '../services/api';

interface Settings {
  review_link: string;
  email_subject: string;
  email_body_html: string;
  auto_enabled: string;
  auto_delay_days: string;
  auto_trigger_event: string;
  place_id: string;
  places_api_configured?: boolean;
}

const TRIGGER_EVENTS = [
  { value: 'supa_networks', label: 'Supa Networks — service active (send_notification step complete)' },
];

const GoogleReviewSettingsTab: React.FC = () => {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<any>(null);
  const [testError, setTestError] = useState<string | null>(null);

  useEffect(() => {
    ApiService.adminGetGoogleReviewSettings()
      .then((data) => setSettings(data))
      .catch(() => setError('Failed to load settings.'))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (field: keyof Settings, value: string) => {
    setSettings((prev) => prev ? { ...prev, [field]: value } : prev);
    setSuccess(null);
    setError(null);
  };

  const handleSave = async () => {
    if (!settings) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await ApiService.adminSaveGoogleReviewSettings({
        review_link: settings.review_link,
        email_subject: settings.email_subject,
        email_body_html: settings.email_body_html,
        auto_enabled: settings.auto_enabled,
        auto_delay_days: settings.auto_delay_days,
        auto_trigger_event: settings.auto_trigger_event,
        place_id: settings.place_id,
      });
      setSuccess('Settings saved.');
      const updated = await ApiService.adminGetGoogleReviewSettings();
      setSettings(updated);
    } catch {
      setError('Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    setTestError(null);
    try {
      const result = await ApiService.adminTestGoogleReviewConnection();
      setTestResult(result);
    } catch (err: any) {
      setTestError(err?.response?.data?.detail || 'Connection test failed.');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;
  }
  if (!settings) {
    return <Alert severity="error">Failed to load Google Review settings.</Alert>;
  }

  const sectionHeading = (text: string) => (
    <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 3, mb: 1, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 11 }}>
      {text}
    </Typography>
  );

  return (
    <Box maxWidth={760}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {sectionHeading('Review Link')}
      <TextField
        label="Google Review Link"
        fullWidth
        size="small"
        value={settings.review_link}
        onChange={(e) => handleChange('review_link', e.target.value)}
        helperText="Direct link customers click to leave a review. Replaces {{review_link}} in the email body."
      />

      <Divider sx={{ my: 3 }} />
      {sectionHeading('Request Email')}
      <TextField
        label="Email Subject"
        fullWidth
        size="small"
        sx={{ mb: 2 }}
        value={settings.email_subject}
        onChange={(e) => handleChange('email_subject', e.target.value)}
        helperText="Available placeholder: {{customer_name}}"
      />
      <TextField
        label="Email Body (HTML)"
        fullWidth
        multiline
        minRows={8}
        size="small"
        value={settings.email_body_html}
        onChange={(e) => handleChange('email_body_html', e.target.value)}
        helperText="Available placeholders: {{customer_name}}, {{review_link}}"
        inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
      />

      <Divider sx={{ my: 3 }} />
      {sectionHeading('Auto-Trigger')}
      <FormControlLabel
        control={
          <Switch
            checked={settings.auto_enabled === 'true'}
            onChange={(e) => handleChange('auto_enabled', e.target.checked ? 'true' : 'false')}
          />
        }
        label="Automatically send review requests"
        sx={{ mb: 2 }}
      />
      <Collapse in={settings.auto_enabled === 'true'}>
        <Box display="flex" gap={2} flexWrap="wrap" sx={{ mb: 1 }}>
          <TextField
            label="Delay (days)"
            size="small"
            type="number"
            sx={{ width: 130 }}
            value={settings.auto_delay_days}
            onChange={(e) => handleChange('auto_delay_days', e.target.value)}
            inputProps={{ min: 0 }}
          />
          <TextField
            label="Trigger event"
            size="small"
            select
            sx={{ flex: 1, minWidth: 260 }}
            value={settings.auto_trigger_event}
            onChange={(e) => handleChange('auto_trigger_event', e.target.value)}
          >
            {TRIGGER_EVENTS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>
        </Box>
        <Typography variant="caption" color="text.secondary">
          One auto request per account — duplicate sends are blocked automatically.
        </Typography>
      </Collapse>

      <Divider sx={{ my: 3 }} />
      {sectionHeading('Google Review Stats (optional — for dashboard)')}
      {!settings.places_api_configured && (
        <Alert severity="warning" sx={{ mb: 2, fontSize: 13 }}>
          <code>REACT_APP_GOOGLE_PLACES_API_KEY</code> is not set in <code>.env</code>. The Places API key is required to fetch your review stats.
        </Alert>
      )}
      <Alert severity="info" sx={{ mb: 2, fontSize: 13 }}>
        Enter your Google Place ID to display your star rating on the dashboard. Uses the same Google Places API key already configured for address autocomplete.{' '}
        <Link href="https://developers.google.com/maps/documentation/javascript/examples/places-placeid-finder" target="_blank" rel="noopener">
          Find your Place ID →
        </Link>
      </Alert>
      <TextField
        label="Google Place ID"
        size="small"
        fullWidth
        sx={{ mb: 2 }}
        value={settings.place_id}
        onChange={(e) => handleChange('place_id', e.target.value)}
        helperText='Starts with "ChIJ…" — find it using the Place ID Finder linked above, or from the Google Maps URL.'
      />

      {testError && <Alert severity="error" sx={{ mb: 2 }}>{testError}</Alert>}
      {testResult && (
        <Alert severity="success" sx={{ mb: 2 }} icon={<StarIcon />}>
          Connected — Average rating: {testResult.average_rating?.toFixed(1) ?? 'N/A'} · Total reviews: {testResult.total_review_count ?? 'N/A'}
        </Alert>
      )}
      <Box display="flex" gap={2} alignItems="center" sx={{ mb: 3 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleTest}
          disabled={testing || !settings.place_id || !settings.places_api_configured}
          startIcon={testing ? <CircularProgress size={14} /> : undefined}
        >
          {testing ? 'Testing…' : 'Test connection'}
        </Button>
        <Typography variant="caption" color="text.secondary">
          Save first, then test the live Places API.
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />
      <Button
        variant="contained"
        onClick={handleSave}
        disabled={saving}
        startIcon={saving ? <CircularProgress size={16} /> : undefined}
      >
        {saving ? 'Saving…' : 'Save settings'}
      </Button>
    </Box>
  );
};

export default GoogleReviewSettingsTab;
